import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { BehaviorSubject, combineLatest, Subject } from 'rxjs';
import { Store } from '@ngrx/store';
import { MatDialog } from '@angular/material/dialog';
import { filter, map, mergeMap, take, takeUntil, tap } from 'rxjs/operators';
import { ActionsSubject } from '@ngrx/store';
import * as _ from 'lodash';

import { State as GroupsState } from 'src/app/core/services/groups/groups.reducer';

import { FormGroup } from '@angular/forms';
import { WriteMessageModalComponent } from 'src/app/shared/components/write-message-modal/write-message-modal.component';
import { AuthService } from 'src/app/core/services/auth.service';
import { NewMessageDialogComponent } from 'src/app/shared/components/new-message-dialog/new-message-dialog.component';
import { IMessageCtx } from '../../shared/components/message/message.component';
import { ActivatedRoute, Router } from '@angular/router';
import { Location } from '@angular/common';
import { MatTabChangeEvent, MatTabGroup } from '@angular/material/tabs';

import { OrganizationFacade } from 'src/app/core/services/organization/organization.facade';
import { Job } from 'src/app/core/services/organization/organization.model';
import { ofType } from '@ngrx/effects';
import {
  createJobSuccess,
  deleteJobSuccess,
  editJobSuccess
} from 'src/app/core/services/organization/organization.actions';
import { MatSnackBar } from '@angular/material/snack-bar';
import { createGroup } from 'src/app/core/services/groups/groups.actions';
import { GroupsFacade } from 'src/app/core/services/groups/groups.facade';
import { GroupLinkService } from 'src/app/shared/services/links';
import { SocketService } from 'src/app/core/services/socket.service';

@Component({
  selector: 'app-organization',
  templateUrl: './organization.component.html',
  styleUrls: ['./organization.component.scss'],
  providers: [OrganizationFacade, GroupsFacade]
})
export class OrganizationComponent implements OnInit, OnDestroy {
  constructor(
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private location: Location,
    private dialog: MatDialog,
    private groupsStore: Store<GroupsState>,
    private readonly authService: AuthService,
    private readonly facade: OrganizationFacade,
    private readonly actionsSubject: ActionsSubject,
    private readonly snackBar: MatSnackBar,
    private readonly groupsFacade: GroupsFacade,
    private readonly groupLinkService: GroupLinkService,
    private readonly _socketService: SocketService
  ) {
    this.onSearch = _.throttle(this.onSearch, 200);
  }

  get isMyOrganization$() {
    return this.facade.organization$.pipe(
      filter((_) => !!_),
      map((_) => this.authService.isMyId(_.id)),
      takeUntil(this.componentDestroy$)
    );
  }

  get isOrgType$() {
    return this.facade.organization$.pipe(
      filter((_) => !!_),
      map((_) => _.org?.member !== 'School'),
      takeUntil(this.componentDestroy$)
    );
  }

  get isSchool$() {
    return this.isOrgType$.pipe(map((_) => !_));
  }

  get isAuthorized$() {
    return this.authService.token$.pipe(map((_) => !!_));
  }

  get posts$() {
    return this.facade.posts$;
  }

  get organization$() {
    return this.facade.organization$;
  }

  get members$() {
    return this.facade.members$;
  }

  get groups$() {
    return this.facade.groups$;
  }

  get jobs$() {
    return this.facade.jobs$;
  }

  get reviews$() {
    return this.facade.reviews$;
  }

  get isShowNewJobForm$() {
    return combineLatest([this.jobToEdit$, this.section$]).pipe(
      takeUntil(this.componentDestroy$),
      map((_) => {
        return _[0] || _[1] === OrganizationComponent.Section.NewJob;
      })
    );
  }

  get isCreatingNewJob$() {
    return this.jobs$.pipe(
      takeUntil(this.componentDestroy$),
      map((_) => _.isCreatingNewJob)
    );
  }
  private componentDestroy$ = new Subject();

  id$: BehaviorSubject<string> = new BehaviorSubject(null);
  section$: BehaviorSubject<OrganizationComponent.Section> =
    new BehaviorSubject(OrganizationComponent.Section.Updates);
  page$: BehaviorSubject<number> = new BehaviorSubject(1);
  search$: BehaviorSubject<string> = new BehaviorSubject(null);
  jobToEdit$: BehaviorSubject<Job> = new BehaviorSubject(null);

  isDefaultBg: boolean;
  reviewForm: FormGroup;

  readonly messageCtx: IMessageCtx = {
    placeholder: `Post an update, article or question`,
    submit: (message) => this.facade.publishPost(message),
    isSuggestArticle: true,
    isSupportTags: true,
    isSupportLinkPreview: true
  };

  @ViewChild(WriteMessageModalComponent, { static: true })
  writeMessageModalComponent: WriteMessageModalComponent;
  @ViewChild('tabs', { static: false }) tabGroup: MatTabGroup;

  ngOnInit(): void {
    this.activatedRoute.params
      .pipe(
        takeUntil(this.componentDestroy$),
        tap((_) => this.id$.next(_.id)),
        tap((_) => {
          const page = Number.isSafeInteger(Number(_.page))
            ? Number(_.page)
            : 1;
          this.page$.next(page);
        }),
        tap((_) => {
          const section = this.parseURLSectionParam(_.section || 'updates');
          this.section$.next(section);
        })
      )
      .subscribe((_) => _);

    this.activatedRoute.queryParams
      .pipe(
        takeUntil(this.componentDestroy$),
        tap((_) => {
          const search = _.search;
          this.search$.next(search);
        })
      )
      .subscribe((_) => _);

    this.section$
      .pipe(takeUntil(this.componentDestroy$))
      .subscribe(this.handleSectionChange);

    combineLatest([this.search$, this.page$])
      .pipe(
        takeUntil(this.componentDestroy$),
        tap((_) => this.search())
      )
      .subscribe((_) => _);

    this.id$
      .pipe(
        takeUntil(this.componentDestroy$),
        tap((id) => this.loadOrganization(id)),
        tap((id) => this.loadReviews(id))
      )
      .subscribe((_) => _);

    this.actionsSubject
      .pipe(
        takeUntil(this.componentDestroy$),
        ofType(createJobSuccess, editJobSuccess, deleteJobSuccess),
        tap((_) => this.section$.next(OrganizationComponent.Section.Jobs)),
        tap((_) => this.jobToEdit$.next(null))
      )
      .subscribe((_) => _);

    this.groupsFacade.newGroupCreated$
      .pipe(
        takeUntil(this.componentDestroy$),
        tap((_) =>
          this.router.navigate(this.groupLinkService.generateGroupLink(_))
        )
      )
      .subscribe((_) => _);
  }

  public onShowNewJobForm() {
    this.section$.next(OrganizationComponent.Section.NewJob);
  }

  public onHideNewJobForm() {
    this.section$.next(OrganizationComponent.Section.Jobs);
    this.jobToEdit$.next(null);
  }

  public onEditJob(job) {
    this.jobToEdit$.next(job);
  }

  public onSubmitJob(job: Job) {
    this.submitJob(job);
  }

  public onPublishReview(obj) {
    this.publishReview(obj);
  }

  public onConnect(id: number) {
    this.connect(id);
  }

  public onDisconnect(id: number) {
    this.disconnect(id);
  }

  public onJoin(id: number) {
    this.join(id);
  }

  public onLeave(id: number) {
    this.leave(id);
  }

  public onCreateClub(club) {
    this.create(club);
  }

  public tabChanged($event: MatTabChangeEvent) {
    this.section$.next(this.indexToSection($event.index));
    this.search$.next(null);
  }

  public openAboutTab() {
    this.section$.next(OrganizationComponent.Section.About);
  }

  public onNewJob() {
    this.section$.next(OrganizationComponent.Section.NewJob);
  }

  public onLoadMoreJobs() {
    this.jobs$
      .pipe(
        takeUntil(this.componentDestroy$),
        take(1),
        tap((_) => this.facade.loadJobs(this.id$.value, _.nextPageToLoad))
      )
      .subscribe((_) => _);
  }

  public async onOpenMessageDialog() {
    this.organization$
      .pipe(
        takeUntil(this.componentDestroy$),
        take(1),
        map((_) => {
          const receiverID = _.dialog_id || null;

          return { receiverID, organization: _ };
        }),
        mergeMap((_) => {
          return this.dialog
            .open(NewMessageDialogComponent, {
              data: { contact: _.organization, receiverID: _.receiverID }
            })
            .afterClosed();
        }),
        takeUntil(this.componentDestroy$),
        take(1),
        filter((_) => _?.isSend && _?.message),
        tap(({ contact, message }): any => this.sendMessage(contact, message))
      )
      .subscribe((_) => _);
  }

  public onSubscribe(id: number) {
    this.subscribe(id);
  }

  public onUnsubscribe(id: number) {
    this.unsubscribe(id);
  }

  inviteEmail({ email, orgId }) {
    this.facade.inviteToProject(email, orgId);
  }

  public onSetPage(page: number) {
    this.page$.next(page);
  }

  public onSearch(term: string) {
    this.search$.next(term);
  }

  ngOnDestroy(): void {
    this.componentDestroy$.next(true);
    this.componentDestroy$.complete();
  }

  mapSectionToIndex(section: OrganizationComponent.Section) {
    switch (section) {
      case OrganizationComponent.Section.Updates:
        return 0;
      case OrganizationComponent.Section.About:
        return 1;
      case OrganizationComponent.Section.Members:
        return 2;
      case OrganizationComponent.Section.Groups:
        return 3;
      case OrganizationComponent.Section.Jobs:
      case OrganizationComponent.Section.NewJob:
        return 4;
      default:
        return 0;
    }
  }

  private parseURLSectionParam(section: string) {
    switch (section) {
      case 'updates':
        return OrganizationComponent.Section.Updates;
      case 'about':
        return OrganizationComponent.Section.About;
      case 'members':
        return OrganizationComponent.Section.Members;
      case 'groups':
        return OrganizationComponent.Section.Groups;
      case 'jobs':
        return OrganizationComponent.Section.Jobs;
      case 'new_job':
        return OrganizationComponent.Section.NewJob;
      default:
        return OrganizationComponent.Section.Updates;
    }
  }

  private indexToSection(index: number) {
    switch (index) {
      case 0:
        return OrganizationComponent.Section.Updates;
      case 1:
        return OrganizationComponent.Section.About;
      case 2:
        return OrganizationComponent.Section.Members;
      case 3:
        return OrganizationComponent.Section.Groups;
      case 4:
        return OrganizationComponent.Section.Jobs;
      default:
        return OrganizationComponent.Section.Updates;
    }
  }

  private handleSectionChange = () => {
    this.page$.next(this.getInitialPageForSection(this.section$.value));
  };

  private search() {
    const section = this.section$.value;
    const page = this.page$.value;
    const search = this.search$.value ? this.search$.value : null;

    this.location.replaceState(
      this.router
        .createUrlTree(
          [
            this.activatedRoute.snapshot.params.id,
            this.activatedRoute.snapshot.params.name,
            section,
            page ? page : undefined
          ].filter((_) => _),
          {
            queryParams: {
              search
            }
          }
        )
        .toString()
    );

    switch (section) {
      case OrganizationComponent.Section.Updates:
        return this.loadPosts();
      case OrganizationComponent.Section.Members:
        return this.loadMembers();
      case OrganizationComponent.Section.Groups:
        return this.loadGroups();
      case OrganizationComponent.Section.Jobs:
        return this.loadJobs();
    }
  }

  private loadOrganization(id: string) {
    this.facade.loadOrganization(id);
  }

  private loadPosts() {
    this.facade.loadPosts(this.id$.value);
  }

  private loadMembers() {
    this.isAuthorized$
      .pipe(
        filter((_) => _),
        take(1),
        tap((_) =>
          this.facade.loadMembers(
            this.id$.value,
            this.page$.value,
            this.search$.value
          )
        ),
        takeUntil(this.componentDestroy$)
      )
      .subscribe((_) => _);
  }

  private loadGroups() {
    this.isAuthorized$
      .pipe(
        filter((_) => _),
        take(1),
        tap((_) =>
          this.facade.loadGroups(
            this.id$.value,
            this.page$.value,
            this.search$.value
          )
        ),
        takeUntil(this.componentDestroy$)
      )
      .subscribe((_) => _);
  }

  private loadJobs() {
    this.isAuthorized$
      .pipe(
        filter((_) => _),
        take(1),
        tap((_) => this.facade.loadJobs(this.id$.value)),
        takeUntil(this.componentDestroy$)
      )
      .subscribe((_) => _);
  }

  private loadReviews(id: string) {
    this.isAuthorized$
      .pipe(
        filter((_) => _),
        take(1),
        tap((_) => this.facade.loadReviews(id)),
        takeUntil(this.componentDestroy$)
      )
      .subscribe((_) => _);
  }

  private submitJob(job: Job) {
    this.isAuthorized$
      .pipe(
        filter((_) => _),
        take(1),
        tap((_) => this.facade.createJob(job)),
        takeUntil(this.componentDestroy$)
      )
      .subscribe((_) => _);
  }

  private deleteJob(job: Job) {
    this.isAuthorized$
      .pipe(
        filter((_) => _),
        take(1),
        tap((_) => this.facade.deleteJob(job)),
        takeUntil(this.componentDestroy$)
      )
      .subscribe((_) => _);
  }

  private subscribe(id: number) {
    this.facade.subscribe(id);
  }

  private unsubscribe(id: number) {
    this.facade.unsubscribe(id);
  }

  // TODO: highly related to contact
  // should be on the contacts facade
  private connect(id: number) {
    this.facade.connect(id);
  }

  private disconnect(id: number) {
    this.facade.disconnect(id);
  }

  // TODO: highly related to group
  // should be on the groups facade
  private join(id: number) {
    this.facade.join(id);
  }

  private leave(id: number) {
    this.facade.leave(id);
  }

  private create(club: {
    school: string;
    address: string;
    [key: string]: any;
  }) {
    this.organization$
      .pipe(
        take(1),
        map((_) => {
          return {
            ...club,
            org_id: _.org?.id,
            school: {
              name: club.school,
              address: club.address
            }
          };
        }),
        tap((_) => this.groupsStore.dispatch(createGroup({ group: _ })))
      )
      .subscribe((_) => _);
  }

  private sendMessage(userID: number, text: string) {
    this._socketService.sendMessage(userID, text, null, null);
    this.messageHasBeenSent();
  }

  private messageHasBeenSent() {
    this.snackBar.open('Message has been sent!', 'OK', {
      duration: 10 * 1000
    });
  }

  private publishReview(review) {
    this.organization$
      .pipe(
        takeUntil(this.componentDestroy$),
        take(1),
        filter((_) => !!_),
        tap((_) => {
          this.facade.publishReview({
            obj_id: _.id,
            text: review.recommendation,
            is_anonymous: review.is_anonymous
          });
        })
      )
      .subscribe((_) => _);
  }

  private getInitialPageForSection(section: OrganizationComponent.Section) {
    switch (section) {
      case OrganizationComponent.Section.Members:
        return 1;
      case OrganizationComponent.Section.Groups:
        return 1;
      default:
        return null;
    }
  }

  onDeleteJob(job: Job) {
    this.deleteJob(job);
  }
}

export namespace OrganizationComponent {
  export enum Section {
    Updates = 'updates',
    About = 'about',
    Members = 'members',
    Groups = 'groups',
    Jobs = 'jobs',
    NewJob = 'new_job'
  }
}
